import { Button, Container } from "react-bootstrap";
import html2pdf from "html2pdf.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment";
import { FetchCaseSummaryDetails } from "../../../../services/SummaryReportApi";
import { RootState } from "../../../../redux/Store";
import Loader from "../../../../common/Loader";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import debounce from "lodash.debounce";
import { formatAppointmentInfo } from "../../../../utils/dateUtils";
type SectionKey =
  | "patient"
  | "case-info"
  | "parties"
  | "team"
  | "injury"
  | "appointment";

const SummaryReport = () => {
  const caseDetails = useSelector((state: RootState) => state.caseHeader);
  const selectedCaseInfoId = caseDetails?.caseInfoId;
  const pdfRef = useRef<HTMLDivElement>(null);
  const sectionRefs: Record<
    SectionKey,
    React.RefObject<HTMLTableRowElement>
  > = {
    patient: useRef<HTMLTableRowElement>(null),
    "case-info": useRef<HTMLTableRowElement>(null),
    parties: useRef<HTMLTableRowElement>(null),
    team: useRef<HTMLTableRowElement>(null),
    injury: useRef<HTMLTableRowElement>(null),
    appointment: useRef<HTMLTableRowElement>(null),
  };

  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [pdf, setPdf] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<SectionKey>("patient");
  const [isScrolling, setIsScrolling] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // const handleScrollToSection = (id: SectionKey, offsetPx: number = 110) => {
  //   const section = sectionRefs[id]?.current;
  //   if (!section) return;

  //   const top = section.getBoundingClientRect().top + window.pageYOffset;
  //   const scrollPosition = top - offsetPx;

  //   window.scrollTo({
  //     top: scrollPosition,
  //     behavior: "smooth",
  //   });
  // };

  // useEffect(() => {
  //   const observer = new IntersectionObserver(
  //     (entries) => {
  //       entries.forEach((entry) => {
  //         if (entry.isIntersecting) {
  //           setActiveSection(entry.target.id as SectionKey);
  //         }
  //       });
  //     },
  //     {
  //       rootMargin: "-20% 0px -70% 0px",
  //       threshold: 0.2,
  //     }
  //   );

  //   Object.values(sectionRefs).forEach((ref) => {
  //     if (ref.current) observer.observe(ref.current);
  //   });

  //   return () => {
  //     Object.values(sectionRefs).forEach((ref) => {
  //       if (ref.current) observer.unobserve(ref.current);
  //     });
  //   };
  // }, []);

  const handleScrollToSection = useCallback(
    (id: SectionKey, offsetPx: number = 110) => {
      const section = sectionRefs[id]?.current;
      if (!section) return;

      setIsScrolling(true);
      const top = section.getBoundingClientRect().top + window.pageYOffset;
      const scrollPosition = top - offsetPx;

      window.scrollTo({
        top: scrollPosition,
        behavior: "smooth",
      });

      // Temporarily disable observer during scroll
      if (observerRef.current) {
        Object.values(sectionRefs).forEach((ref) => {
          if (ref.current) observerRef.current?.unobserve(ref.current);
        });
      }

      // Re-enable after scroll completes
      setTimeout(() => {
        setIsScrolling(false);
        if (observerRef.current) {
          Object.values(sectionRefs).forEach((ref) => {
            if (ref.current) observerRef.current?.observe(ref.current);
          });
        }
      }, 1000);
    },
    []
  );

  const initObserver = useCallback(() => {
    if (observerRef.current) {
      // Clean up existing observer
      Object.values(sectionRefs).forEach((ref) => {
        if (ref.current) observerRef.current?.unobserve(ref.current);
      });
    }

    const observerOptions = {
      root: null,
      rootMargin: "-30% 0px -50% 0px", // Adjusted margins
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      if (isScrolling) return;

      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id as SectionKey);
        }
      });
    }, observerOptions);

    // Observe all sections
    Object.entries(sectionRefs).forEach(([key, ref]) => {
      if (ref.current) {
        ref.current.id = key; // Ensure ID is set
        observer.observe(ref.current);
      }
    });

    observerRef.current = observer;
  }, [isScrolling]);

  const handleScroll = debounce(() => {
    if (isScrolling) return;

    // Find which section is currently visible
    Object.entries(sectionRefs).forEach(([key, ref]) => {
      if (ref.current) {
        const rect = ref.current.getBoundingClientRect();
        if (rect.top <= 150 && rect.bottom >= 150) {
          // Middle of viewport
          setActiveSection(key as SectionKey);
        }
      }
    });
  }, 100);

  useEffect(() => {
    // Wait for content to load
    const timer = setTimeout(() => {
      initObserver();

      // Add scroll listener as fallback
      window.addEventListener("scroll", handleScroll);
    }, 500);

    return () => {
      clearTimeout(timer);
      if (observerRef.current) {
        Object.values(sectionRefs).forEach((ref) => {
          if (ref.current) observerRef.current?.unobserve(ref.current);
        });
      }
      window.removeEventListener("scroll", handleScroll);
    };
  }, [initObserver, handleScroll]);

  // Re-initialize when summary data loads
  useEffect(() => {
    if (summary) {
      initObserver();
    }
  }, [summary, initObserver]);

  useEffect(() => {
    if (!selectedCaseInfoId) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        const { result } = await FetchCaseSummaryDetails(selectedCaseInfoId);
        setSummary(result);
      } catch (err: any) {
        // setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedCaseInfoId]);
  const getBase64FromUrl = async (url: string): Promise<string> => {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        if (typeof reader.result === "string") {
          resolve(reader.result);
        } else {
          reject("Failed to convert blob to base64");
        }
      };
      reader.onerror = () => reject("Failed to read blob");
      reader.readAsDataURL(blob);
    });
  };
  const generatePDF = async () => {
    setLoading(true);

    const input = document.getElementById("pdfRef")!;
    const originalFontSize = input.style.fontSize;
    input.style.fontSize = "16px";

    const canvas = await html2canvas(input, {
      scale: 2, // high quality
      useCORS: true,
    });

    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth(); // 210mm
    const pdfHeight = pdf.internal.pageSize.getHeight(); // 297mm

    const margin = 10;
    const headerHeight = 20;
    const footerHeight = 15;
    const contentHeight = pdfHeight - headerHeight - footerHeight;

    const canvasWidth = canvas.width;
    const canvasHeight = canvas.height;

    const pdfContentWidth = pdfWidth - 2 * margin;
    const scaleFactor = pdfContentWidth / canvasWidth;
    
    const logoPath = summary?.logo?.trim() ? summary.logo : "/logo.png";
    const logoBase64 = await getBase64FromUrl(logoPath);

    // 👇 Snap canvas slice height to nearest multiple of row height
    const approxRowHeight = 60; // Adjust this value based on your row size (try 36, 40, or 44)
    let pageHeightInCanvasPx = contentHeight / scaleFactor;
    pageHeightInCanvasPx =
      Math.floor(pageHeightInCanvasPx / approxRowHeight) * approxRowHeight;

    let yOffsetPx = 0;
    let pageNumber = 1;

    while (yOffsetPx < canvasHeight) {
      const pageCanvas = document.createElement("canvas");
      pageCanvas.width = canvasWidth;
      pageCanvas.height = Math.min(
        pageHeightInCanvasPx,
        canvasHeight - yOffsetPx
      );

      const ctx = pageCanvas.getContext("2d")!;
      ctx.drawImage(
        canvas,
        0,
        yOffsetPx,
        canvasWidth,
        pageCanvas.height,
        0,
        0,
        canvasWidth,
        pageCanvas.height
      );

      const pageImg = pageCanvas.toDataURL("image/png");

      if (pageNumber > 1) pdf.addPage();

      // 👉 Header
      pdf.setFontSize(12);
      pdf.text("Medical Legal Portal", margin, 14);
      pdf.addImage(logoBase64, "PNG", pdfWidth - margin - 34, 5, 34, 12);

      // 👉 Content
      const imageHeightInMm = pageCanvas.height * scaleFactor;
      pdf.addImage(
        pageImg,
        "PNG",
        margin,
        headerHeight,
        pdfContentWidth,
        imageHeightInMm
      );

      // 👉 Footer
      const now = new Date();
      const formattedDate = now.toLocaleString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });

      const footerY = pdfHeight - 10;
      const generatedByText = `Generated By & On - ${summary.generatedBy} ${formattedDate}`;
      const pageNumberText = `Page No. ${pageNumber}`;
      const pageNumberTextWidth = pdf.getTextWidth(pageNumberText);

      pdf.setDrawColor(0, 0, 0);
      pdf.setLineWidth(0.1);
      pdf.line(margin, footerY - 5, pdfWidth - margin, footerY - 5);
      pdf.setFontSize(8);
      pdf.text(generatedByText, margin, footerY);
      pdf.text(
        pageNumberText,
        pdfWidth - margin - pageNumberTextWidth,
        footerY
      );

      // Move to next slice
      yOffsetPx += pageHeightInCanvasPx;
      pageNumber++;
    }

    input.style.fontSize = originalFontSize;
    pdf.save("Summary_Report.pdf");
    setLoading(false);
  };

  if (error) return <p className="text-danger m-4">Error: {error}</p>;
  if (!summary) return <p className="m-4"></p>;
  const displayValue = (value: any) => {
    if (value === null || value === undefined || value === "") {
      return "N/A";
    }
    return value;
  };

  function maskFaxNumber(fax: any) {
    if (!fax) return "";
    const cleanFax = fax.replace(/\D/g, ""); // remove all non-digits
    if (cleanFax.length < 4) return fax;
    const lastFour = cleanFax.slice(-4);
    return `XXX-XXX-${lastFour}`;
  }
  function maskSSN(ssn: any) {
    if (!ssn) return "";
    // Remove non-digit characters first
    const digits = ssn.replace(/\D/g, "");
    if (digits.length !== 9) return ssn; // if not 9 digits, return as is
    return `XXX-XX-${digits.slice(5)}`;
  }
  function maskFaxNumberSubject(fax: any) {
    if (!fax) return "";
    // Remove non-digit characters
    const digits = fax.replace(/\D/g, "");
    if (digits.length < 4) return fax; // If too short, return as is
    // Mask everything except last 4 digits
    const maskedPart = digits.slice(0, -4).replace(/\d/g, "X");
    const visiblePart = digits.slice(-4);
    return maskedPart + visiblePart;
  }
  function formatDate(dateString: string): string {
    if (!dateString) return "";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";

    const month = String(date.getMonth() + 1).padStart(2, "0"); // months are 0-based
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();

    return `${month}/${day}/${year}`; // MM/DD/YYYY
  }
  const safeParse = (json: string | null) => {
    try {
      return json ? JSON.parse(json) : [];
    } catch {
      return [];
    }
  };
  const renderContacts = (contacts: any[]) => {
    if (!contacts || contacts.length === 0) return <div>N/A</div>;

    return contacts.map((c, i) => (
      <div key={i}>
        <div>{c.name}</div>
        <div>{c.email}</div>
        <div>
          {c.phoneNumber1} | {c.phoneNumber2}
        </div>
      </div>
    ));
  };

  return (
    <Container fluid className="d-flex mt-4" style={{ position: "relative" }}>
      <Loader isLoading={loading} />

      <div className="flex-grow-1" style={{ marginRight: "30px" }}>
        <div ref={pdfRef} id="pdfRef" style={{ padding: "0px 10 0pxpx" }}>
          <table className="clean-table pb-5 clean-table mb-3">
            <tbody>
              <tr id="patient" ref={sectionRefs["patient"]}>
                <td colSpan={2} className="section-header">
                  Patient
                </td>
              </tr>
              <tr>
                <td className="summarycell">Patient Name</td>
                <td className="summarycelltext">
                  {displayValue(summary.subjectFullName)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Social Security Number(SSN)</td>
                <td className="summarycelltext">
                  {displayValue(
                    summary.caseSubjectSsn
                      ? maskSSN(summary.caseSubjectSsn)
                      : ""
                  )}
                </td>
              </tr>
              <tr>
                <td className="summarycell">AKA</td>
                <td className="summarycelltext">
                  {displayValue(summary.caseSubjectAka)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Gender</td>
                <td className="summarycelltext">
                  {displayValue(summary.caseSubjectGenderDesc)}
                </td>
              </tr>

              <tr>
                <td className="summarycell">Date of Birth</td>
                <td className="summarycelltext">
                  {displayValue(
                    moment(summary.caseSubjectDob).format("MM/DD/YYYY")
                  )}
                </td>
              </tr>

              <tr>
                <td className="summarycell">Primary Address</td>
                <td className="summarycelltext">
                  {displayValue(summary.caseSubjectAddress)}
                </td>
              </tr>

              <tr>
                <td className="summarycell">Email Address</td>
                <td className="summarycelltext">
                  {displayValue(summary.caseSubjectEmailAddress)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Phone No</td>
                <td className="summarycelltext">
                  {displayValue(summary.caseSubjectPhoneNumber)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Fax No</td>
                <td className="summarycelltext">
                  {displayValue(summary.caseSubjectFaxNumber || "")}
                </td>
              </tr>
              {/* Case Info */}
              <tr id="case-info" ref={sectionRefs["case-info"]}>
                <td colSpan={2} className="section-header">
                  Case Information
                </td>
              </tr>
              <tr>
                <td className="summarycell">Case ID</td>
                <td className="summarycelltext">
                  {displayValue(summary.caseId)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Claim Number</td>
                <td className="summarycelltext">
                  {displayValue(summary.claimNumber)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Date of Registration</td>
                <td className="summarycelltext">
                  {displayValue(
                    moment(summary.dateOfRegistration).format("MM/DD/YYYY")
                  )}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Priority</td>
                <td className="summarycelltext">{summary.priorityName}</td>
              </tr>
              <tr>
                <td className="summarycell">Case (Review) Type</td>
                <td className="summarycelltext">
                  {displayValue(summary.caseReviewTypeName)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">WCAB No</td>
                <td className="summarycelltext">
                  {displayValue(summary.wcabNumber)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Panel No</td>
                <td className="summarycelltext">
                  {displayValue(summary.panelNumber)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">File No</td>
                <td className="summarycelltext">
                  {displayValue(summary.fileNumber)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">ADJ No</td>
                <td className="summarycelltext">
                  {displayValue(summary.adjNumber)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Case Number</td>
                <td className="summarycelltext">
                  {displayValue(summary.caseNumber)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Case Name</td>
                <td className="summarycelltext">
                  {displayValue(summary.caseName)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Comments</td>
                <td className="summarycelltext">
                  {displayValue(summary.additionalComments)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Case Stage</td>
                <td className="summarycelltext">
                  {displayValue(summary.caseStageName)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Created By</td>
                <td className="summarycelltext">
                  {displayValue(summary.createdByName)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Created On</td>
                <td className="summarycelltext">
                  {displayValue(
                    moment(summary.createdDate).format("MM/DD/YYYY")
                  )}
                </td>
              </tr>
              {/* Stakeholders */}
              <tr id="parties" ref={sectionRefs["parties"]}>
                <td colSpan={2} className="section-header">
                  Parties
                </td>
              </tr>

              <tr>
                <td className="summarycell theme-color">Client Name</td>
                <td className="summarycelltext">
                  {displayValue(summary.clientName)}
                </td>
              </tr>

              <tr>
                <td className="summarycell">Client Address</td>
                <td className="summarycelltext">
                  {displayValue(summary.clientAddress)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Client Contacts</td>
                <td className="summarycelltext">
                  {summary.clientJson && summary.clientJson.length > 0 ? (
                    summary.clientJson.map((contact: any, index: any) => (
                      <div key={index} style={{ marginBottom: "0.5rem" }}>
                        <div>
                          {contact.ContactFullName || "N/A"} |{" "}
                          {contact.Email || "N/A"} |{""}
                          {contact.Phone || "N/A"}|{" "}
                          {contact.Fax ? ` | ${contact.Fax}` : ""}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div>N/A</div>
                  )}
                </td>
              </tr>
              <tr>
                <td className="summarycell theme-color">Applicant Attorney</td>
                <td className="summarycelltext">
                  {displayValue(summary.applicantAttorneyName)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Applicant Attorney Address</td>
                <td className="summarycelltext">
                  {displayValue(summary.applicantAttorneyAddress)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Applicant Attorney Contacts</td>
                <td className="summarycelltext">
                  {summary.applicantAttorney &&
                  summary.applicantAttorney.length > 0 ? (
                    summary.applicantAttorney.map(
                      (contact: any, index: any) => (
                        <div key={index} style={{ marginBottom: "0.5rem" }}>
                          <div>
                            {contact.ContactFullName || "N/A"} |{""}{" "}
                            {contact.Email || "N/A"} |{""}
                            {""}
                            {contact.Phone || "N/A"} {""}
                            {""}| {""}
                            {""}
                            {contact.Fax ? ` | ${contact.Fax}` : ""}
                          </div>
                        </div>
                      )
                    )
                  ) : (
                    <div>N/A</div>
                  )}
                </td>
              </tr>
              <tr>
                <td className="summarycell theme-color">Defense Attorney</td>
                <td className="summarycelltext">
                  {displayValue(summary.defenceAttorneyName)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Defense Attorney Address</td>
                <td className="summarycelltext">
                  {displayValue(summary.defenceAttorneyAddress)}
                </td>
              </tr>

              <tr>
                <td
                  colSpan={2}
                  style={{ background: "#fff", border: "0px", padding: "0px" }}
                ></td>
              </tr>

              <tr>
                <td className="summarycell">Defense Attorney Contacts</td>

                <td className="summarycelltext">
                  {summary.defenceAttorneyJson &&
                  summary.defenceAttorneyJson.length > 0 ? (
                    summary.defenceAttorneyJson.map(
                      (contact: any, index: any) => (
                        <div key={index} style={{ marginBottom: "0.5rem" }}>
                          <div>
                            {contact.ContactFullName || "N/A"} |{" "}
                            {contact.Email || "N/A"} | {contact.Phone || "N/A"}{" "}
                            {contact.Fax || "N/A"}
                          </div>
                        </div>
                      )
                    )
                  ) : (
                    <div>N/A</div>
                  )}
                </td>
              </tr>
              <tr>
                <td className="summarycell theme-color">Insurance (Claims)</td>
                <td className="summarycelltext">
                  {displayValue(summary.insuranceAdminName)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Insurance (Claims) Address</td>
                <td className="summarycelltext">
                  {displayValue(summary.insuranceAdminAddress)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Insurance (Claims) Contacts</td>
                <td className="summarycelltext">
                  {summary.insuranceAdminJson &&
                  summary.insuranceAdminJson.length > 0 ? (
                    summary.insuranceAdminJson.map(
                      (contact: any, index: any) => (
                        <div key={index} style={{ marginBottom: "0.5rem" }}>
                          <div>
                            {contact.ContactFullName || "N/A"} |{" "}
                            {contact.Email || "N/A"} |{" "}
                            {displayValue(contact.Phone || "N/A")}{" "}
                            {contact.Fax ? ` | ${contact.Fax}` : ""}
                          </div>
                        </div>
                      )
                    )
                  ) : (
                    <div>{displayValue(summary.insuranceAdminPhoneNumber)}</div>
                  )}
                </td>
              </tr>
              <tr>
                <td className="summarycell theme-color">Service Provider</td>
                <td className="summarycelltext">
                  {summary.serviceProviderName}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Service Provider Address</td>
                <td className="summarycelltext">
                  {displayValue(summary.serviceProviderAddress)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Service Provider Contacts</td>
                <td className="summarycelltext">
                  {summary.serviceProviderJson &&
                  summary.serviceProviderJson.length > 0 ? (
                    summary.serviceProviderJson.map(
                      (contact: any, index: any) => (
                        <div key={index} style={{ marginBottom: "0.5rem" }}>
                          <div>
                            {contact.ContactFullName || "N/A"} |{" "}
                            {contact.Email || "N/A"} |{" "}
                            {displayValue(contact.Phone || "N/A")}{" "}
                            {contact.Fax ? ` | ${contact.Fax}` : ""}
                          </div>
                        </div>
                      )
                    )
                  ) : (
                    <div>
                      {displayValue(summary.serviceProviderPhoneNumber)}
                    </div>
                  )}
                </td>
              </tr>

              <tr>
                <td className="summarycell theme-color">Employer</td>
                <td className="summarycelltext">
                  {displayValue(summary.employerEntityName)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Employer Address</td>
                <td className="summarycelltext">
                  {displayValue(summary.employerAddress)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Employer Contacts</td>
                <td className="summarycelltext">
                  {summary.employerJson && summary.employerJson.length > 0 ? (
                    summary.employerJson.map((contact: any, index: any) => (
                      <div key={index} style={{ marginBottom: "0.5rem" }}>
                        <div>
                          {contact.ContactFullName || "N/A"} |{" "}
                          {contact.Email || "N/A"} |{" "}
                          {displayValue(contact.Phone) || "N/A"}{" "}
                          {contact.Fax ? ` | ${contact.Fax}` : ""}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div>{displayValue(summary.empStkAddressPhoneNumber)}</div>
                  )}
                </td>
              </tr>
              {/* Team */}
              <tr id="team" ref={sectionRefs["team"]}>
                <td colSpan={2} className="section-header">
                  Team
                </td>
              </tr>

              <tr>
                <td className="summarycell">Team Name</td>
                <td className="summarycelltext">
                  {displayValue(summary.managedByTeam)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Team Leader</td>
                <td className="summarycelltext">
                  {displayValue(summary.managedByTeamHodName)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Phone No</td>
                <td className="summarycelltext">
                  {displayValue(summary.teamHodPhone)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Email Address</td>
                <td className="summarycelltext">
                  {displayValue(summary.teamHodEmailId)}
                </td>
              </tr>
              <tr>
                <td className="summarycell">Fax No</td>
                <td className="summarycelltext">
                  {displayValue(summary.teamHodFax)}
                  {/* {displayValue(
                    summary.teamHodFax ? maskFaxNumber(summary.teamHodFax) : ""
                  )} */}
                </td>
              </tr>

              {/* Subject */}

              {/* Injury */}
              <tr id="injury" ref={sectionRefs["injury"]}>
                <td colSpan={2} className="section-header">
                  Injury
                </td>
              </tr>
              {summary.injuries?.length > 0 ? (
                summary.injuries.map((injury: any, index: number) => (
                  <>
                    <tr key={`injury-title-${index}`}>
                      <td className="summarycell theme-color">
                        Injury {index + 1}
                      </td>
                      <td className="summarycelltext"></td>
                    </tr>
                    <tr>
                      <td className="summarycell">Date of Incident</td>
                      <td className="summarycelltext">
                        {injury.InjuryType?.includes("Continious Trauma (CT)")
                          ? injury.CTStartDate && injury.CTEndDate
                            ? `[${formatDate(
                                injury.CTStartDate
                              )} to ${formatDate(injury.CTEndDate)}]`
                            : ""
                          : injury.DateOfIncident
                          ? formatDate(injury.DateOfIncident)
                          : ""}
                      </td>
                    </tr>
                    <tr>
                      <td className="summarycell">Injury Type</td>
                      <td className="summarycelltext">
                        {" "}
                        {injury.InjuryType?.split("(")[0]?.trim()}
                      </td>
                    </tr>
                    <tr>
                      <td className="summarycell">Claim No</td>
                      <td className="summarycelltext">
                        {displayValue(injury.ClaimNumber)}
                      </td>
                    </tr>
                    <tr>
                      <td className="summarycell">Severity of Injury</td>
                      <td className="summarycelltext">
                        {displayValue(injury.Severity)}
                      </td>
                    </tr>
                    <tr>
                      <td className="summarycell">Nature of Injury</td>
                      <td className="summarycelltext">
                        {displayValue(injury.NatureOfInjury)}
                      </td>
                    </tr>
                    <tr>
                      <td className="summarycell">Injury Details</td>
                      <td className="summarycelltext">
                        {displayValue(injury.Details)}
                      </td>
                    </tr>
                    <tr>
                      <td className="summarycell">Additional Comments</td>
                      <td className="summarycelltext">
                        {displayValue(injury.Comments)}
                      </td>
                    </tr>

                    <tr>
                      <td className="summarycell">Body Part(s) Affected</td>
                      <td className="summarycelltext">
                        {displayValue(injury.BodyPartsAffected)}
                      </td>
                    </tr>

                    <tr>
                      <td className="summarycell">
                        Additional Body Parts Affected
                      </td>
                      <td className="summarycelltext">
                        {displayValue(injury.AdditionalBodyParts)}
                      </td>
                    </tr>
                  </>
                ))
              ) : (
                <tr>
                  <td>No injuries found.</td>
                  <td className="summarycelltext"></td>
                </tr>
              )}
              <tr id="appointment" ref={sectionRefs["appointment"]}>
                <td colSpan={2} className="section-header">
                  Appointment
                </td>
              </tr>
              {summary.appointments?.length > 0 ? (
                summary.appointments.map((appointment: any, index: number) => (
                  <>
                    <tr key={`injury-title-${index}`}>
                      <td className="summarycell theme-color">
                        Appointment {index + 1}
                      </td>
                      <td className="summarycelltext"></td>
                    </tr>

                    <tr>
                      <td className="summarycell">Date of Appointment</td>

                      <td className="summarycelltext">
                        {displayValue(
                          moment(appointment.DateOfAppointment).format(
                            "MM/DD/YYYY"
                          )
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="summarycell">Doctor Name</td>

                      <td className="summarycelltext">
                        {displayValue(appointment.DoctorName)}
                      </td>
                    </tr>

                    <tr>
                      <td className="summarycell">Appointment Information</td>
                      <td className="summarycelltext">
                        {formatAppointmentInfo(appointment.AppointmentInfo) || "N/A"}
                      </td>
                    </tr>
                    <tr>
                      <td className="summarycell">Appointment Number</td>
                      <td className="summarycelltext">
                        {displayValue(appointment.AppointmentConfirmationNo)}
                      </td>
                    </tr>
                    <tr>
                      <td className="summarycell">Appointment Status</td>
                      <td className="summarycelltext">
                        {displayValue(appointment.AppointmentStatusDesc)}
                      </td>
                    </tr>
                    <tr>
                      <td className="summarycell">Medical Exam</td>
                      <td className="summarycelltext">
                        {displayValue(appointment.MedicareProviderAddress)}
                      </td>
                    </tr>
                  </>
                ))
              ) : (
                <tr>
                  <td>No Appointments found.</td>
                  <td className="summarycelltext"></td>
                </tr>
              )}
              <tr>
                <td
                  colSpan={2}
                  style={{
                    background: "#fff",
                    border: "0px",
                    padding: "10px 0px",
                  }}
                ></td>
              </tr>
            </tbody>
          </table>
          {/* {pdf &&(
            <>
              <div className='border-top'>
                    <div
                      style={{
                        float: 'left',

                        display: 'inline-block',
                        marginTop: 5,
                        fontSize: 12,
                      }}
                    >
                      Generated By & On - JOHN DOE 05/05/2025 14:53
                    </div>
                    <div
                      style={{
                        float: 'right',
                        width: 100,
                        display: 'inline-block',
                        marginTop: 5,
                        fontSize: 12,
                      }}
                    >
                      <div style={{textAlign: 'right'}}>Page No. 1</div>
                    </div>
                    <div style={{clear: 'both', height: 1}}>&nbsp;</div>
                  </div>
            </>)} */}
        </div>
      </div>

      {/* Right Side Navigation */}
      <div
        style={{
          width: "220px",
          position: "sticky",
          top: "150px",
          right: "20px",
          zIndex: 999,
          height: "73vh",
        }}
      >
        <div className="border shadow-sm summary-section d-flex flex-column">
          <ul className="list-unstyled small">
            {(Object.keys(sectionRefs) as SectionKey[]).map((key) => (
              <li key={key}>
                <button
                  className={`btn btn-link p-0 ${
                    activeSection === key ? "fw-bold text-primary" : ""
                  }`}
                  onClick={() => {
                    handleScrollToSection(key);
                    setActiveSection(key);
                  }}
                >
                  {key
                    .replace(/-/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </button>
              </li>
            ))}
          </ul>
          <Button
            variant=""
            className="mt-3 mt-auto border-0"
            onClick={generatePDF}
          >
            <span className="material-symbols-outlined material-icons mx-1 btn-primary text-white p-2 rounded-circle">
              download
            </span>
          </Button>
        </div>
      </div>

      {/* Custom styles */}
      <style>{`
        .clean-table {
          width: 100%;
        }
        .clean-table td {
          border: none;
          padding: 8px 12px;
          vertical-align: top;
          font-weight: 500;
        }
        .clean-table tr:not(:last-child) td {
          border-bottom: 1px solid #dee2e6;
        }
        .section-header {
          background-color: #0092d5;
          color: white;
          font-weight: bold;
          padding: 10px 12px;
          text-align: left;
          border-bottom: none;
        }
        .label {
          font-weight: bold;
          width: 40%;
          white-space: nowrap;
        }
        .clean-table tr:nth-child(odd) {
          background-color:  #ffffff; /* Light gray for odd rows */
        }
       
        .clean-table tr:nth-child(even) {
          background-color:#f9f9f9; /* White for even rows */
        }
      `}</style>
    </Container>
  );
};

export default SummaryReport;
